import {Capacitor} from "@capacitor/core";
import {NativeAudio} from "@capgo/capacitor-native-audio";

export default class SoundManager
{
    static isNative = Capacitor.isNativePlatform();
    static audioElements = {};

    static path(assetId)
    {
        return `assets/audio/${assetId}.mp3`;
    }

    static register(assetId, element)
    {
        this.audioElements[assetId] = element;
    }

    static async preload(assetId)
    {
        if (this.isNative) {
            await NativeAudio.preload({
                assetId,
                assetPath: this.path(assetId),
                isUrl: false,
            });
        }
    }

    static setMusicVolume(assetId, volume)
    {
        if (this.isNative) {
            NativeAudio.setVolume({assetId, volume: volume / 100});
            return;
        }
        this.audioElements[assetId].volume = volume / 100;
    }

    static async playMusic(assetId)
    {
        // Native plays through the OS audio stack, so it is not subject to the
        // WebView autoplay policy and can start from launch without a user gesture.
        if (this.isNative) {
            const {isPlaying} = await NativeAudio.isPlaying({assetId});
            if (! isPlaying) {
                await NativeAudio.loop({assetId});
            }
            return;
        }
        // Web: the browser blocks play() until a user gesture, so this may reject
        // on launch. The route watcher retries on navigation (a real interaction).
        if (this.audioElements[assetId].paused) {
            this.audioElements[assetId].play().catch(() => {});
        }
    }

    static async playSFX(assetId, volume = 100)
    {
        if (this.isNative) {
            const level = volume / 100;
            try {
                // Ensure the asset is loaded once. The `volume` option on
                // preload is not honored by the native plugin, and a
                // setVolume() issued before play() does not reliably reach the
                // media instance that actually plays (on Android the instance
                // volume is (re)initialized on play, so the earlier setVolume is
                // lost). The only value that applies per playback is the
                // `volume` passed directly to play(), so we preload without a
                // level and let play() set it.
                await NativeAudio.preload({
                    assetId,
                    assetPath: this.path(assetId),
                    isUrl: false,
                }).catch(() => { /* already preloaded */ });
                await NativeAudio.play({assetId, volume: level});
            } catch {
                // Asset may be unavailable; nothing to play.
            }
            return;
        }
        // Web: create a temporary Audio element for one-shot playback
        const audio = new Audio('/' + this.path(assetId));
        audio.volume = volume / 100;
        audio.play().catch(() => {});
    }

    static async stop(assetId)
    {
        if (this.isNative) {
            await NativeAudio.stop({assetId}).catch(() => {});
            await NativeAudio.unload({assetId}).catch(() => {});
        }
    }
}
